import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Users, Palette, Settings, Presentation } from 'lucide-react';
import { chProjectService } from '../services';
import { ChTeamTab } from '../components/ChTeamTab';
import { ChBrandingTab } from '../components/ChBrandingTab';
import { ChSettingsTab } from '../components/ChSettingsTab';
import { ContentTab } from '../components/ContentTab';
import type { ChProject } from '../types';

type Tab = 'conteudo' | 'equipa' | 'branding' | 'definicoes';

const TABS: { key: Tab; label: string; icon: typeof FileText }[] = [
  { key: 'conteudo', label: 'Conteúdo', icon: FileText },
  { key: 'equipa', label: 'Equipa', icon: Users },
  { key: 'branding', label: 'Branding', icon: Palette },
  { key: 'definicoes', label: 'Definições', icon: Settings },
];

export function ChProjectDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<ChProject | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('conteudo');

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    chProjectService.getBySlug(slug).then(p => {
      setProject(p);
      setLoading(false);
    });
  }, [slug]);

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-gray-200 rounded" />
          <div className="h-12 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-4 md:p-8">
        <p className="text-gray-500">Projeto não encontrado.</p>
        <button onClick={() => navigate('/contenthub/projetos')} className="ch-btn ch-btn-ghost mt-4">
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </button>
      </div>
    );
  }


  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto ch-animate-in">
      <button
        onClick={() => navigate('/contenthub/projetos')}
        className="ch-btn ch-btn-ghost ch-btn-sm mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Projetos
      </button>

      <h1 className="ch-heading ch-heading-lg mb-6 flex items-center gap-3">
        {project.name}
        <button
          onClick={() => navigate(`/contenthub/projeto/${project.slug}/apresentacao`)}
          className="ch-btn ch-btn-sm ch-btn-ghost"
          title="Modo apresentação"
        >
          <Presentation className="w-4 h-4" />
          Apresentação
        </button>
      </h1>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 overflow-x-auto pb-1">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`ch-btn ch-btn-sm whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-[var(--ch-ink)] text-white'
                : 'ch-btn-ghost'
            }`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'conteudo' && <ContentTab project={project} />}

      {activeTab === 'equipa' && <ChTeamTab project={project} />}
      {activeTab === 'branding' && <ChBrandingTab project={project} />}
      {activeTab === 'definicoes' && <ChSettingsTab project={project} />}
    </div>
  );
}
